import React, { useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Check, ExternalLink, MessageSquare, CheckCircle, X, Megaphone, Download, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useNotifications } from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';
import { formatTimeAgo } from '@/lib/formatDate';
import type { Notification } from '@/types/notifications';

function getNotificationIcon(notification: Notification) {
  if (notification.title.includes('proposta')) return MessageSquare;
  if (notification.title.includes('mensagem')) return MessageSquare;
  if (notification.title.includes('concluída')) return CheckCircle;
  if (notification.title.includes('cancelada')) return X;
  return Megaphone;
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getRoleLabel(role: string): string {
  const labels: Record<string, string> = {
    admin: 'Admin',
    staff: 'Staff',
    moderator: 'Moderador',
    partner: 'Parceiro',
    student: 'Estudante',
    user: 'Usuário',
    visitor: 'Visitante'
  };
  return labels[role] || role;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead } = useNotifications();
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    // Admin notifications open in modal
    if (notification.type === 'admin') {
      setDropdownOpen(false);
      setSelectedNotification(notification);
      setShowDetailModal(true);
      return;
    }

    // System notifications (negotiation, etc.) redirect directly
    if (notification.action_url) {
      setDropdownOpen(false);
      if (notification.action_url.startsWith('http://') || notification.action_url.startsWith('https://')) {
        window.open(notification.action_url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(notification.action_url);
      }
    }
  };

  const handleModalAction = () => {
    if (selectedNotification?.action_url) {
      if (selectedNotification.action_url.startsWith('http://') || selectedNotification.action_url.startsWith('https://')) {
        window.open(selectedNotification.action_url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(selectedNotification.action_url);
        setShowDetailModal(false);
      }
    }
  };

  const handleDownloadAttachment = () => {
    if (selectedNotification?.attachment_url) {
      window.open(selectedNotification.attachment_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="relative h-9 w-9 rounded-lg hover:bg-secondary p-0"
          >
            <Bell className="w-4 h-4 text-muted-foreground" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 rounded-xl p-0">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="font-semibold text-sm">Notificações</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-primary hover:text-primary"
                onClick={(e) => {
                  e.preventDefault();
                  markAllAsRead();
                }}
              >
                <Check className="w-3 h-3 mr-1" />
                Marcar todas
              </Button>
            )}
          </div>

          {/* Notifications List */}
          <ScrollArea className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="sm" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 px-4">
                <Bell className="w-10 h-10 text-muted-foreground/50 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  Nenhuma notificação
                </p>
              </div>
            ) : (
            <div className="py-1">
                {notifications.map((notification) => {
                  const Icon = getNotificationIcon(notification);
                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={cn(
                        "flex gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-secondary/50",
                        !notification.is_read && "bg-primary/5"
                      )}
                    >
                      <div className={cn(
                        "flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center",
                        !notification.is_read ? "bg-primary/10" : "bg-secondary"
                      )}>
                        <Icon className={cn(
                          "w-4 h-4",
                          !notification.is_read ? "text-primary" : "text-muted-foreground"
                        )} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn(
                          "text-sm leading-tight mb-0.5",
                          !notification.is_read ? "font-medium text-foreground" : "text-muted-foreground"
                        )}>
                          {notification.title}
                        </p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-[10px] text-muted-foreground">
                            {formatTimeAgo(notification.created_at)}
                          </span>
                          {notification.type === 'admin' && notification.sender?.display_name && (
                            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                              <Avatar className="w-4 h-4">
                                <AvatarImage src={notification.sender.avatar_url || undefined} />
                                <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
                                  {notification.sender.display_name?.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              {notification.sender.display_name}
                              {notification.sender.role && (
                                <span className="px-1 py-0.5 bg-primary/10 text-primary rounded text-[9px] uppercase font-medium">
                                  {getRoleLabel(notification.sender.role)}
                                </span>
                              )}
                            </span>
                          )}
                          {notification.action_url && notification.type !== 'admin' && (
                            <span className="text-[10px] text-primary flex items-center gap-0.5">
                              <ExternalLink className="w-2.5 h-2.5" />
                              {notification.action_label || 'Ver'}
                            </span>
                          )}
                        </div>
                      </div>
                      {!notification.is_read && (
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          {notifications.length > 0 && (
            <>
              <DropdownMenuSeparator className="m-0" />
              <div className="p-2">
                <Link to="/notifications">
                  <Button variant="ghost" size="sm" className="w-full text-xs justify-center">
                    Ver todas as notificações
                  </Button>
                </Link>
              </div>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Detail Modal for Admin Notifications */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-primary" />
              Mensagem da Equipe
            </DialogTitle>
          </DialogHeader>

          {selectedNotification && (
            <div className="space-y-4">
              {/* Image */}
              {selectedNotification.image_url && (
                <div className="rounded-lg overflow-hidden border border-border">
                  <img
                    src={selectedNotification.image_url}
                    alt="Imagem da notificação"
                    className="w-full h-auto max-h-48 object-cover"
                  />
                </div>
              )}

              {/* Title & Message */}
              <div>
                <h4 className="font-semibold text-lg mb-2">
                  {selectedNotification.title}
                </h4>
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {selectedNotification.message}
                </p>
              </div>

              {/* Sender & Time */}
              <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                {selectedNotification.sender?.display_name && (
                  <div className="flex items-center gap-2">
                    <Avatar className="w-6 h-6">
                      <AvatarImage src={selectedNotification.sender.avatar_url || undefined} />
                      <AvatarFallback className="text-xs bg-primary/10 text-primary">
                        {selectedNotification.sender.display_name?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span>{selectedNotification.sender.display_name}</span>
                    {selectedNotification.sender.role && (
                      <span className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-xs uppercase font-medium">
                        {getRoleLabel(selectedNotification.sender.role)}
                      </span>
                    )}
                  </div>
                )}
                <span className="text-xs">{formatDateTime(selectedNotification.created_at)}</span>
              </div>

              {/* Attachment */}
              {selectedNotification.attachment_url && (
                <div className="p-3 bg-secondary/50 rounded-lg border border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <Download className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm truncate">
                        {selectedNotification.attachment_name || 'Anexo'}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadAttachment}
                    >
                      Baixar
                    </Button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDetailModal(false)}
                >
                  Fechar
                </Button>
                {selectedNotification.action_url && (
                  <Button
                    className="flex-1"
                    onClick={handleModalAction}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {selectedNotification.action_label || 'Acessar'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
